import React, { useEffect } from "react";
import { Grid, styled } from "@mui/material";
import { IconButton } from "../IconButton/IconButton";
import ExpandMoreSharp from "@mui/icons-material/ExpandMoreSharp";
import ExpandLessSharp from "@mui/icons-material/ExpandLessSharp";
import Tooltip from "@mui/material/Tooltip";
import classNames from "classnames";
import { EventChip, IEventChip } from "./EventChip";

type IEventGroup = IEventChip[];
export interface IEventGroupChip {
  data?: IEventGroup;
  groupKey?: string;
  onClickGroup?: (groupKey: string) => void;
  onClickChip?: (eventId: string) => void;
  collapseOnClickChip?: boolean;
  buttonAlignRight?: boolean;
  hideGroupCount?: boolean;
  clickGroupToToggle?: boolean;
  groupIsExpanded?: (state: boolean) => void;
}

const OCEventGroupChip = styled(Grid)(({ theme }) => ({
  userSelect: "none",
  width: 120,
  height: 26,
  boxShadow: "2px 2px 5px " + theme.palette.common.black,
  justifyContent: "space-between",
  display: "flex",
  flexDirection: "row",
  margin: "2px",
  borderRadius: "2px",
  fontFamily: "B612 Mono, arial, sans-serif", // Cousine showed poor anti-aliasing (blurred)
  fontSize: 10,
  fontWeight: "normal",
  padding: "5px",
  alignItems: "center",
  color: theme.palette.primary.contrastText, // This get's overriden by the inline style applied to assign best contrast color.
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.shortest,
  }),
  overflow: "hidden",
  position: "relative",
  "&.cancelled": {
    opacity: 0.75,
    userSelect: "none",
    "& .cancelled_icon": {
      marginLeft: "-5px",
    },
  },
  "&.status_bar": {
    paddingTop: "2px",
    paddingBottom: "3px",
    boxShadow: "1px 1px 2px " + theme.palette.common.black,
    "& .origin, & .destination , & .flightnumber, & .event_title": {
      height: "auto",
      marginTop: "3px",
    },
  },
  "&.draggable": {
    cursor: "grab",
    "&:active": { cursor: "grabbing" },
  },
  "& div": {
    display: "flex",
    flexDirection: "row",
    padding: "0px 5px",
  },
  "& .details": {
    flexDirection: "column",
    "& div": { lineHeight: "9px", padding: 0 },
  },
  "& .aircraft_icon": {
    marginTop: "-2px",
    transform: "rotate(50deg) scale(1.4)",
    opacity: 0.2,
    "& path": { fill: "black" },
  },
  "& .info": {
    position: "absolute",
    width: 110,
    "& .flightnumber, & .event_title": {
      fontSize: 12,
      fontWeight: "bold",
      padding: "0px 2px",
    },
    "& .origin, & .destination ": {
      padding: "0px 2px",
    },
  },
  "&.interactive": {
    "&:hover": { margin: "2px 0px", width: 124, cursor: "pointer" },
    "&:active": { margin: "2px 4px", width: 116 },
  },
}));

/**
 * Move selected event to the top of the event array.
 * @param data -
 * @param eventId -
 */
const bringToFront = (data: IEventGroup, eventId: string): IEventGroup => {
  return data.sort((a: any, b: any) => (a._id === eventId ? -1 : b._id === eventId ? 1 : 0));
};

// EVENT GROUP CHIP
export const EventGroupChip = (props: IEventGroupChip): JSX.Element => {
  const [expand, setExpand] = React.useState<boolean>(false);
  const { data, onClickChip, collapseOnClickChip, buttonAlignRight, hideGroupCount, groupIsExpanded, onClickGroup } =
    props;
  const [selectedChip, setSelectedChip] = React.useState<string>(null);
  const groupId = "eventgroup";
  const [sortedGroupChips, setSortedGroupChips] = React.useState<IEventGroup>(data);

  // Update parent on status of group's expanded state.
  useEffect(() => {
    if (groupIsExpanded) groupIsExpanded(expand);
  }, [groupIsExpanded, expand]);

  useEffect(() => {
    const selectedEvent = selectedChip ? selectedChip : data[0]?._id;
    const sortedEvent = bringToFront(data, selectedEvent);
    setSelectedChip(selectedEvent);
    setSortedGroupChips(sortedEvent);
  }, [data, selectedChip]);

  /**
   * fire off function passed in from parent when user clicks on a chip group.
   */
  const handleOnClickGroup = (event: any): void => {
    const { onClickGroup, groupKey, clickGroupToToggle } = props;
    if (sortedGroupChips.length > 1 && clickGroupToToggle) setExpand(!expand);
    // If handler is passed in from props, fire it off
    if (typeof onClickGroup === "function" && !!groupKey && !expand) {
      onClickGroup(groupKey);
    }
  };

  // Function to resort group and send select chipId to parent.
  // If collapseOnClickChip is true, will resort the group so that the selected chip appears at the top.
  const handleOnClickChip = (eventId: string): void => {
    if (!onClickChip || typeof onClickChip !== "function") {
      return;
    }
    if (collapseOnClickChip) {
      setExpand(false);
      setSelectedChip(eventId);
    }
    onClickChip(eventId);
  };

  // This function is used only when the group count button is displayed.
  const handleToggleExpand = (event: any): void => {
    setExpand(!expand);
    if (props.groupIsExpanded) props.groupIsExpanded(!expand); // update parent.
  };

  if (!!sortedGroupChips && sortedGroupChips.length > 0) {
    // Remove max expanded height for now.
    const maxCollapsedCount = sortedGroupChips.length <= 5 ? sortedGroupChips.length : 5;
    const groupHeightCollapsed: number = 28 + maxCollapsedCount * 3;

    const maxExpandedCount = sortedGroupChips.length;
    const groupHeightExpanded: number = 28 * maxExpandedCount + 2;

    return (
      <OCEventGroupChip
        container
        key={groupId}
        id={groupId}
        className={classNames(expand ? "expanded" : "collapsed", buttonAlignRight && "expandRight")}
        style={{
          height: `${expand ? groupHeightExpanded : groupHeightCollapsed}px`,
          width: hideGroupCount ? 124 : 150,
        }}
      >
        {sortedGroupChips.length > 1 && !hideGroupCount && (
          <IconButton id="toggle_expand_btn" className="toggle_expand_button" onClick={handleToggleExpand} size="large">
            {!expand ? <ExpandMoreSharp /> : <ExpandLessSharp />}
            <div className="group_count">{sortedGroupChips.length > 0 && sortedGroupChips.length}</div>
          </IconButton>
        )}

        <Tooltip title={`Group Event Count: ${sortedGroupChips.length}`} enterDelay={1000} placement="top">
          <Grid
            item
            className={classNames("chips", !!onClickGroup && !expand && "interactive")}
            onClick={handleOnClickGroup}
          >
            {sortedGroupChips.map((chipData: any, index: number) => {
              return (
                <div
                  key={`${chipData._id}-flightChip`}
                  className={classNames("flightchip_container", index < 5 ? `chip_${index}` : "chip_N")}
                >
                  <EventChip data={chipData} onClickChip={expand ? handleOnClickChip : null} />
                </div>
              );
            })}
          </Grid>
        </Tooltip>
      </OCEventGroupChip>
    );
  }
  return null;
};
